import { Component, JSX, Show } from 'solid-js';
import { TbTrendingUp, TbTrendingDown } from 'solid-icons/tb';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: () => JSX.Element;
  trend?: number; // Percentage change
  trendLabel?: string;
  loading?: boolean;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'indigo';
}

const colorClasses = {
  blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
  red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
};

/**
 * Format number with thousand separators
 */
const formatNumber = (value: string | number): string => {
  let str = value.toString();

  // Handle currency strings (like "$278.48")
  if (str.includes('$')) {
    const dollarSign = str.substring(0, str.indexOf('$') + 1);
    const amount = str.substring(str.indexOf('$') + 1);
    // Add thousand separators to the numeric part
    const formatted = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return dollarSign + formatted;
  }

  // Add thousand separators for plain numbers
  return str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * StatsCard Component
 * Displays a metric card with optional trend indicator
 */
const StatsCard: Component<StatsCardProps> = (props) => {
  const color = () => props.color || 'indigo';
  const formattedValue = () => formatNumber(props.value);

  return (
    <div class="bg-body dark:bg-gray-800 rounded-lg shadow-card p-6">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <p class="text-sm font-medium text-gray-500 dark:text-gray-400 text-heading-style font-body">{props.title}</p>
          <Show
            when={!props.loading}
            fallback={
              <div class="mt-2 h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            }
          >
            <p class="mt-2 text-3xl font-bold text-gray-900 dark:text-white font-body">{formattedValue()}</p>
          </Show>

          {/* Trend Indicator */}
          <Show when={props.trend !== undefined}>
            <div class="mt-2 flex items-center gap-1">
              <Show
                when={props.trend! >= 0}
                fallback={
                  <>
                    <TbTrendingDown size={16} class="text-red-500" />
                    <span class="text-sm font-medium text-red-600 dark:text-red-400 font-body">
                      {Math.abs(props.trend!)}%
                    </span>
                  </>
                }
              >
                <TbTrendingUp size={16} class="text-green-500" />
                <span class="text-sm font-medium text-green-600 dark:text-green-400 font-body">
                  {props.trend}%
                </span>
              </Show>
              {props.trendLabel && (
                <span class="text-sm text-gray-500 dark:text-gray-400 font-body">{props.trendLabel}</span>
              )}
            </div>
          </Show>
        </div>

        {/* Icon */}
        <Show when={props.icon}>
          <div class={`p-3 rounded-lg ${colorClasses[color()]}`}>{props.icon!()}</div>
        </Show>
      </div>
    </div>
  );
};

export default StatsCard;
